// frontend/src/components/wizard/StepMapping/SourceFieldPickerModal.jsx
import React, { useState, useMemo } from 'react';
import { Search, X, CheckSquare, Square, Plus } from 'lucide-react';
import { useLanguageStore } from '../../../store/useLanguageStore';

export const SourceFieldPickerModal = ({ isOpen, onClose, onConfirm, availableColumns }) => {
  const { t } = useLanguageStore();
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return availableColumns;
    const q = search.toLowerCase();
    return availableColumns.filter(c => c.name.toLowerCase().includes(q));
  }, [availableColumns, search]);

  const allFilteredSelected = filtered.length > 0 && filtered.every(c => selected.has(c.name));

  const toggleAll = () => {
    if (allFilteredSelected) {
      // Deseleccionar solo los filtrados visibles
      setSelected(prev => {
        const next = new Set(prev);
        filtered.forEach(c => next.delete(c.name));
        return next;
      });
    } else {
      setSelected(prev => {
        const next = new Set(prev);
        filtered.forEach(c => next.add(c.name));
        return next;
      });
    }
  };

  const toggle = (name) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const handleConfirm = () => {
    const selectedCols = availableColumns.filter(c => selected.has(c.name));
    onConfirm(selectedCols);
    setSelected(new Set());
    setSearch('');
  };

  const handleClose = () => {
    setSelected(new Set());
    setSearch('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">

        {/* ── Header ──────────────────────────────────────────────── */}
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <div>
            <h3 className="text-lg font-bold text-slate-900">
              {t('wizard.mapping.add_source_fields_title') || 'Agregar campos del origen'}
            </h3>
            <p className="text-sm text-slate-500 mt-0.5">
              {t('wizard.mapping.add_source_fields_desc') || 'Selecciona los campos adicionales que deseas incluir en el destino.'}
            </p>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-slate-100 rounded-xl transition">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* ── Search + Select all ─────────────────────────────────── */}
        <div className="px-5 pt-4 pb-2 space-y-2">
          {availableColumns.length > 6 && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('wizard.mapping.search_placeholder') || 'Buscar campo...'}
                className="w-full pl-9 pr-8 py-2 text-sm border border-slate-200 rounded-xl bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 transition"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          )}

          <button
            onClick={toggleAll}
            className="flex items-center gap-2 text-sm text-slate-600 hover:text-indigo-700 font-medium px-1 py-1 transition"
          >
            {allFilteredSelected
              ? <CheckSquare className="w-4 h-4 text-indigo-600" />
              : <Square className="w-4 h-4 text-slate-400" />
            }
            {allFilteredSelected
              ? (t('wizard.mapping.deselect_all') || 'Deseleccionar todos')
              : (t('wizard.mapping.select_all') || 'Seleccionar todos')
            }
            <span className="text-slate-400 text-xs">({filtered.length})</span>
          </button>
        </div>

        {/* ── Column list ─────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-5 py-2 space-y-0.5 min-h-0">
          {filtered.map(col => {
            const isChecked = selected.has(col.name);
            return (
              <label
                key={col.name}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition ${isChecked ? 'bg-indigo-50 border border-indigo-200' : 'hover:bg-slate-50 border border-transparent'}`}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => toggle(col.name)}
                  className="sr-only"
                />
                {isChecked
                  ? <CheckSquare className="w-4 h-4 text-indigo-600 shrink-0" />
                  : <Square className="w-4 h-4 text-slate-300 shrink-0" />
                }
                <span className={`text-sm font-medium truncate ${isChecked ? 'text-indigo-900' : 'text-slate-700'}`}>
                  {col.name}
                </span>
                <span className="text-[11px] text-slate-400 ml-auto shrink-0 uppercase">
                  {col.type || 'VARCHAR'}
                </span>
              </label>
            );
          })}

          {filtered.length === 0 && (
            <p className="text-center text-slate-400 py-8 text-sm">
              {search.trim()
                ? (t('wizard.mapping.no_fields_found') || 'No se encontraron campos.')
                : (t('wizard.mapping.no_extra_fields') || 'Todas las columnas del origen ya están en el destino.')
              }
            </p>
          )}
        </div>

        {/* ── Footer ──────────────────────────────────────────────── */}
        <div className="flex items-center justify-between p-5 border-t border-slate-100">
          <span className="text-sm text-slate-500">
            <strong className="text-slate-700">{selected.size}</strong>{' '}
            {t('wizard.mapping.fields_selected') || 'seleccionados'}
          </span>
          <div className="flex gap-2">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition"
            >
              {t('common.buttons.cancel') || 'Cancelar'}
            </button>
            <button
              onClick={handleConfirm}
              disabled={selected.size === 0}
              className="inline-flex items-center px-4 py-2 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl transition shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              {t('wizard.mapping.add_fields_btn') || 'Agregar'} ({selected.size})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
